import { useTranslation } from 'react-i18next'
import { Loader, ShieldAlert } from 'lucide-react'
import { PermissionEntityType } from '@/api/model/permissionEntityType'
import { Checkbox } from '@/components/shadcn/checkbox'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { cn } from '@/lib/utils'
import {
  type CategorizedPermission,
  type Permission,
  type SubcategoryItem,
  isEntityCategory,
  getPermissionNameLabel,
} from '../permissions.types'

interface Props {
  selectedUserId: string | null
  selectedCategory: PermissionEntityType | null
  permissions: CategorizedPermission | undefined
  items: SubcategoryItem[]
  userPermissions: Record<string, boolean>
  // Keys with a toggle mutation currently in flight → spinner instead of checkbox in the row.
  pendingKeys: Set<string>
  // Permissions of the selected user are loading.
  isLoading: boolean
  // Loading the permissions failed.
  isError: boolean
  onToggle: (perm: Permission, entityId?: number) => void
  // The displayed user is the logged-in user themselves.
  isSelf: boolean
  // The logged-in user may change permissions (Permissions.Edit).
  canEdit: boolean
  // Render only the global permissions, without the per-object sections. Used by the
  // administrator page: a TargetEntityId belongs to one company, and an administrator switches
  // between companies, so such an assignment would be inert everywhere else.
  globalOnly?: boolean
}

export function PermissionListPanel({
  selectedUserId, selectedCategory, permissions, items, userPermissions, pendingKeys, isLoading, isError, onToggle, isSelf, canEdit, globalOnly = false,
}: Props) {
  const { t } = useTranslation('permissions')
  // A checkbox row. entityId set → per-object permission, otherwise global.
  // If the global permission is set, every object inherits it (on + locked).
  const renderRow = (perm: Permission, entityId?: number) => {
    const globalKey = `${perm.entityType}.${perm.permissionName}`
    const key = entityId != null ? `${globalKey}.${entityId}` : globalKey
    const parentChecked = entityId != null ? (userPermissions[globalKey] ?? false) : false
    const checked = (userPermissions[key] ?? false) || parentChecked
    // You may not revoke your own permission management (BON-1852).
    // Mirrors the backend: neither company permission management nor administrator permission
    // management can be taken from yourself. Neither appears on the company page.
    const selfLocked = isSelf &&
      (perm.entityType === PermissionEntityType.Permissions ||
        perm.entityType === PermissionEntityType.AdministratorPermissions)
    // Is this row's save mutation still running?
    const pending = pendingKeys.has(key)
    // Without Permissions.Edit all checkboxes are read-only; locked while saving.
    const disabled = parentChecked || selfLocked || !canEdit || pending
    return (
      <div key={key} className="flex items-center gap-3">
        {pending ? (
          <Loader className="size-4 shrink-0 animate-spin text-primary" />
        ) : (
          <Checkbox
            id={key}
            checked={checked}
            disabled={disabled}
            onCheckedChange={() => onToggle(perm, entityId)}
          />
        )}
        <label
          htmlFor={key}
          title={selfLocked ? t('messages.selfLockedTooltip') : undefined}
          className={cn(
            'text-sm cursor-pointer select-none',
            disabled && 'text-muted-foreground cursor-not-allowed'
          )}
        >
          {getPermissionNameLabel(t, perm.permissionName)}
        </label>
      </div>
    )
  }

  // A section (heading + rows); empty sections are omitted. Keyed by entityId, not by title:
  // shop module headings are type labels and repeat when a company has two modules of a type.
  const renderSection = (title: string, perms: Permission[], entityId?: number) => {
    if (perms.length === 0) return null
    return (
      <div key={entityId ?? title} className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-muted-foreground">{title}</p>
        {perms.map(p => renderRow(p, entityId))}
      </div>
    )
  }

  const renderContent = () => {
    const global = permissions?.global ?? []
    const perEntity = permissions?.perEntity ?? []
    const emptyMsg = <p className="text-sm text-muted-foreground italic">{t('messages.noPermissions')}</p>

    // Object category: "General" section (global) + one section per object.
    if (!globalOnly && selectedCategory && isEntityCategory(selectedCategory)) {
      return (
        <div className="flex flex-col gap-6">
          {renderSection(t('messages.generalSection'), global)}
          {items.map(item => renderSection(item.name, perEntity, item.id))}
        </div>
      )
    }

    // Normal category: only global permissions, flat without a heading.
    if (global.length === 0) return emptyMsg
    return <div className="flex flex-col gap-3">{global.map(p => renderRow(p))}</div>
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <div className="px-4 py-3 border-b bg-muted/40">
        <p className="text-sm font-semibold">{t('panels.permissions.title')}</p>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-custom p-4">
        {!selectedUserId ? (
          <p className="text-sm text-muted-foreground italic">{t('messages.selectUser')}</p>
        ) : isLoading ? (
          <LoaderGeneral isLoading loadingMessage={t('messages.loading')} />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <ShieldAlert className="size-12 opacity-30" />
            <p className="text-sm">{t('messages.loadError')}</p>
          </div>
        ) : !selectedCategory ? (
          <p className="text-sm text-muted-foreground italic">{t('messages.selectCategory')}</p>
        ) : (
          // key per category forces a fresh mount on switch — otherwise React recycles
          // the differently-structured children and leaves sections standing.
          <div key={selectedCategory}>{renderContent()}</div>
        )}
      </div>
    </div>
  )
}
